import React from "react";

import { useShopQuery } from "@hooks/use-shop-query.js";
import { gql } from "graphql-request";
import {getGidFromRestId} from "@utility/shopify";

const queryVariant = gql`
  query ProductAndCollection($country:CountryCode!, $variantId:ID!) @inContext(country: $country){
    node(id: $variantId) {
      ... on ProductVariant {
        title,
        id,
        sku,
        available: availableForSale,
        options: selectedOptions{
          name,
          value
        }
        price {
          amount
        }
        compareAtPrice {
          amount
        }
        compatibilityKeys: metafield(namespace: "compatibility", key: "keys") {
          value
        },
        includedItems: metafield(namespace: "included", key: "product_variants") {
          value,
          references(first: 8) {
            edges {
              node {
                ...on ProductVariant {
                  title
                  id
                  sku
                  image{
                    url
                    altText
                  }
                  price {
                    amount
                  }
                }
              }
            }
          }
        }
        customizerCollections: metafield(namespace: "customizer", key: "collections") {
          references(first: 10) {
            edges {
              node {
                ... on Collection {
                  id
                  handle
                  title
                  descriptionHtml
                  customizerTitle: metafield(namespace: "customizer", key: "title") {
                    value
                  }
                  products(first: 100) {
                    edges {
                      node {
                        ...CustomizerItem
                      }
                    }
                  }
                }
              }
            }
          }
        }
        product {
          title,
          cartNotes: metafields(identifiers: [
            {namespace: "cart-notes", key: "page"}
            {namespace: "cart-notes", key: "rules"}
            {namespace: "cart-notes", key: "priority"}
          ]) {
            key
            value
          }
          includedItemsTitle: metafield(namespace: "accentuate", key: "included_item_title") {
            value
          }
          customizerCollections: metafield(namespace: "customizer", key: "collections") {
            references(first: 10) {
              edges {
                node {
                  ... on Collection {
                    id
                    handle
                    title
                    descriptionHtml
                    customizerTitle: metafield(namespace: "customizer", key: "title") {
                      value
                    }
                    products(first: 100) {
                      edges {
                        node {
                          ...CustomizerItem
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }

  fragment CustomizerItem on Product {
		title,
		id,
		handle,
		available: availableForSale,
		description,
		title,
		type: productType,
		options{
			id,
			name,
			values
		}
		price: priceRange{
			maxVariantPrice {
				amount
			},
			minVariantPrice {
				amount
			}
		}
		image:featuredImage{
			url,
			altText
		}
		additionalOptions: metafield(namespace:"custom",key:"additional_product_options") {
			value
			references (first:100){
				edges {
					node {
						... on Metaobject {
							id,
							label: field(key:"label"){
								value
							}
							sublabel: field(key:"sublabel"){
								value
							}
							variant: field(key:"variant"){
								reference{
									... on ProductVariant{
										id
										compatibilityKeys: metafield(namespace:"compatibility",key:"keys") {
											value
										}
									}
								}
							}
							compatibility: field(key:"compatibility"){
								value
							}
						}
					}
				}
			}
		}
		usesVehiclePicker: metafield(namespace:"compatibility",key:"uses_vehicle_data") {
			value
		}
		variants(first:100){
			edges{
				node{
					id,
					title,
					sku,
					available: availableForSale,
					options: selectedOptions{
						name,
						value
					}
					price {
						amount
					}
					image {
						url,
						altText
					}
					compatibilityKeys: metafield(namespace:"compatibility",key:"keys") {
						value
					}
				}
			}
		}
	}`;

export const ProductCustomizerVariantContext = React.createContext({
  loading: true
});

export const ProductCustomizerVariantProvider = ({children, productHandle, variantId}) => {

  const variables = { variantId: getGidFromRestId(variantId, "ProductVariant"), country: Shopify.country }
  const {data, loading:productLoading, error} = useShopQuery(queryVariant, variables);

  const variant = data?.node;

  if(variant){variant.includedItemsTitle = data?.product?.includedItemsTitle};

  const compatibilityKeyString = variant?.compatibilityKeys?.value;

  let compatibilityKeys;
  if(compatibilityKeyString){
    try{
      compatibilityKeys = JSON.parse(compatibilityKeyString);
    }catch(e){
      console.error(e);
      console.error(`Unable to parse compatibilityKeys from ${variant.id}`);
    }
  }

  return <ProductCustomizerVariantContext.Provider value={{ loading:productLoading, variant, product: data?.node?.product, compatibilityKeys }}>
    {children}
    </ProductCustomizerVariantContext.Provider>;
}
